import { ArrowLeft, ArrowRight, Check, CircleDollarSign, House, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { applicationSchema, californiaDate, createEmptyApplication, leadSourceFromQuery, TURNSTILE_ACTION, type ApplicationInput } from "../lib/application";

declare global {
	interface Window {
		turnstile?: {
			render(container: HTMLElement, options: {
				sitekey: string;
				action: typeof TURNSTILE_ACTION;
				appearance: "interaction-only";
				execution: "render";
				size: "flexible";
				callback(token: string): void;
				"expired-callback"(): void;
				"error-callback"(errorCode: string): void;
				"timeout-callback"(): void;
				"unsupported-callback"(): void;
			}): string;
			remove(widgetId: string): void;
			reset(widgetId: string): void;
		};
	}
}

type TurnstileStatus =
	| { state: "loading" | "verified" }
	| { state: "error"; message: string };

const choices = [
	{ value: "purchase" as const, icon: House, title: "Purchase", detail: "Rental Property • Fix & Flip • Bridge Loan" },
	{ value: "refinance" as const, icon: RefreshCw, title: "Refinance / Business-Purpose Cash-Out", detail: "For proceeds primarily used for a business or investment purpose" },
	{ value: "other" as const, icon: CircleDollarSign, title: "Other", detail: "For a transaction that does not cleanly fit the first two choices" },
];

const useOptions = [
	"Real estate investment or acquisition",
	"Fix & flip / resale",
	"Property renovation or construction",
	"Business investment or expenses",
	"Bridge financing related to a business or investment transaction",
	"Other business or investment purpose",
];

function currency(value: number): string {
	return value ? `$${Math.round(value).toLocaleString("en-US")}` : "";
}

function fromCurrency(value: string): number {
	return Number(value.replace(/\D/g, "")) || 0;
}

function formatPhone(value: string): string {
	const digits = value.replace(/\D/g, "").slice(0, 10);
	if (digits.length <= 3) return digits;
	if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function FieldError({ message }: { message?: string }) {
	return message ? <span className="field-error" role="alert">{message}</span> : null;
}

export function Turnstile({ onToken, onStatus, retryNonce }: {
	onToken(token: string): void;
	onStatus(status: TurnstileStatus): void;
	retryNonce: number;
}) {
	const container = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
		if (!sitekey || !container.current) {
			onToken("");
			onStatus({ state: "error", message: "We couldn't load the security verification. Please refresh the page and try again." });
			return;
		}
		let active = true;
		let widgetId = "";
		let loadTimer = 0;
		let retryTimer = 0;
		const clearToken = () => onToken("");
		const reset = () => {
			if (!active || !widgetId || !window.turnstile) return;
			clearToken();
			onStatus({ state: "loading" });
			window.turnstile.reset(widgetId);
		};
		const recover = () => {
			window.clearTimeout(retryTimer);
			retryTimer = window.setTimeout(reset, 2_000);
		};
		const render = () => {
			if (!container.current || !window.turnstile || widgetId) return;
			window.clearTimeout(loadTimer);
			try {
				widgetId = window.turnstile.render(container.current, {
					sitekey,
					action: TURNSTILE_ACTION,
					appearance: "interaction-only",
					execution: "render",
					size: "flexible",
					callback: (token) => { onToken(token); onStatus({ state: "verified" }); },
					"expired-callback": () => { reset(); },
					"error-callback": (errorCode) => {
						console.warn("Turnstile challenge failed", { errorCode });
						clearToken();
						onStatus({ state: "error", message: "We couldn't complete the security verification. Please try again." });
						recover();
					},
					"timeout-callback": reset,
					"unsupported-callback": () => {
						clearToken();
						onStatus({ state: "error", message: "This browser can't complete the security verification. Please update your browser or try another current browser." });
					},
				});
			} catch {
				clearToken();
				onStatus({ state: "error", message: "We couldn't initialize the security verification. Please refresh the page and try again." });
			}
		};
		const priorScript = document.querySelector<HTMLScriptElement>('script[src*="challenges.cloudflare.com/turnstile"]');
		if (priorScript?.dataset.coxTurnstileFailed === "true") priorScript.remove();
		const existing = priorScript?.dataset.coxTurnstileFailed === "true" ? null : priorScript;
		const script = existing || document.createElement("script");
		const failedToLoad = () => {
			script.dataset.coxTurnstileFailed = "true";
			clearToken();
			onStatus({ state: "error", message: "We couldn't load the security verification. Please refresh the page or temporarily disable content blocking for this site and try again." });
		};
		script.addEventListener("load", render, { once: true });
		script.addEventListener("error", failedToLoad, { once: true });
		if (!existing) {
			script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
			script.async = true;
			script.defer = true;
			script.dataset.coxTurnstile = "true";
			document.head.appendChild(script);
		}
		onStatus({ state: "loading" });
		render();
		loadTimer = window.setTimeout(() => { if (!widgetId) failedToLoad(); }, 12_000);
		return () => {
			active = false;
			window.clearTimeout(loadTimer);
			window.clearTimeout(retryTimer);
			script.removeEventListener("load", render);
			script.removeEventListener("error", failedToLoad);
			if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
		};
	}, [onStatus, onToken, retryNonce]);
	return <div className="turnstile-field" aria-label="Security verification"><div ref={container} /></div>;
}

export default function ApplicationForm() {
	const environment = import.meta.env.VITE_APP_ENVIRONMENT;
	const isProduction = environment === "PRODUCTION";
	const isReleaseCandidate = environment === "RELEASE_CANDIDATE";
	const finalVisual = isProduction || isReleaseCandidate;
	const [step, setStep] = useState<1 | 2 | 3>(1);
	const [data, setData] = useState<ApplicationInput>(() => {
		const application = createEmptyApplication();
		const source = new URLSearchParams(window.location.search).get("source");
		application.leadSource = leadSourceFromQuery(source);
		return application;
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState(false);
	const [consent, setConsent] = useState(false);
	const [applicationId, setApplicationId] = useState("");
	const [submissionError, setSubmissionError] = useState("");
	const [savedIncomplete, setSavedIncomplete] = useState(false);
	const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus>({ state: "loading" });
	const [turnstileRetryNonce, setTurnstileRetryNonce] = useState(0);
	const [pendingSecuritySubmission, setPendingSecuritySubmission] = useState(false);

	const valueLabel = data.transactionType === "purchase" ? "Purchase Price" : "Estimated Property Value";
	const minDate = useMemo(() => californiaDate(), []);
	const handleTurnstileToken = useCallback((token: string) => {
		setData((current) => ({ ...current, turnstileToken: token }));
		if (token) setErrors((current) => ({ ...current, turnstileToken: "" }));
	}, []);
	const handleTurnstileStatus = useCallback((status: TurnstileStatus) => setTurnstileStatus(status), []);

	useEffect(() => {
		if (isReleaseCandidate) {
			setData((current) => ({ ...current, challengeToken: "release-candidate-mock", turnstileToken: "release-candidate-mock" }));
			return;
		}
		let active = true;
		fetch("/api/applications/challenge", { headers: { Accept: "application/json" }, credentials: "same-origin" })
			.then(async (response) => {
				if (!response.ok) throw new Error("challenge unavailable");
				return response.json() as Promise<{ challengeToken: string }>;
			})
			.then(({ challengeToken }) => { if (active) setData((current) => ({ ...current, challengeToken })); })
			.catch(() => { if (active) setSubmissionError(`The ${finalVisual ? "" : "test "}application service is unavailable. Please try again later.`); });
		return () => { active = false; };
	}, [finalVisual, isReleaseCandidate]);

	function update<K extends keyof ApplicationInput>(key: K, value: ApplicationInput[K]) {
		setData((current) => ({ ...current, [key]: value }));
		setErrors((current) => ({ ...current, [key]: "" }));
	}

	function chooseTransaction(transactionType: ApplicationInput["transactionType"]) {
		setData((current) => ({ ...current, transactionType }));
		setErrors({});
	}

	function updateAddress(index: number, value: string) {
		const next = [...data.propertyAddresses]; next[index] = value; update("propertyAddresses", next);
	}

	function addAddress() {
		if (data.propertyAddresses.length < 10) update("propertyAddresses", [...data.propertyAddresses, ""]);
	}

	function removeAddress(index: number) {
		update("propertyAddresses", data.propertyAddresses.filter((_, itemIndex) => itemIndex !== index));
	}

	function continueToDetails() {
		setStep(2); setErrors({});
		requestAnimationFrame(() => document.getElementById("application-heading")?.focus());
	}

	function validateBeforeSecurity(): boolean {
		const parsed = applicationSchema.safeParse({ ...data, turnstileToken: data.turnstileToken || "pending-security-verification" });
		if (!parsed.success || !consent) {
			const nextErrors: Record<string, string> = {};
			for (const issue of parsed.success ? [] : parsed.error.issues) nextErrors[String(issue.path[0])] = issue.message;
			if (!consent) nextErrors.consent = "Please acknowledge this statement.";
			setErrors(nextErrors);
			requestAnimationFrame(() => document.querySelector<HTMLElement>(".field-error")?.closest("label, fieldset")?.scrollIntoView({ behavior: "smooth", block: "center" }));
			return false;
		}
		return true;
	}

	async function submitVerifiedApplication() {
		const parsed = applicationSchema.safeParse(data);
		if (!parsed.success || !consent) {
			setPendingSecuritySubmission(false);
			validateBeforeSecurity();
			return;
		}
		setSubmitting(true);
		setPendingSecuritySubmission(false);
		setSubmissionError("");
		try {
			if (isReleaseCandidate) {
				await new Promise((resolve) => setTimeout(resolve, 450));
				setStep(3);
				requestAnimationFrame(() => document.getElementById("confirmation-heading")?.focus());
				return;
			}
			const response = await fetch("/api/applications", {
				method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(parsed.data),
			});
			const result = await response.json() as { applicationId?: string; error?: string; issues?: Record<string, string[]> };
			if (!response.ok) {
				if (response.status === 403) {
					setData((current) => ({ ...current, turnstileToken: "" }));
					setTurnstileStatus({ state: "error", message: "We couldn't complete the security verification. Please try again." });
					setTurnstileRetryNonce((current) => current + 1);
					setSubmissionError("");
					return;
				}
				if (result.issues) setErrors(Object.fromEntries(Object.entries(result.issues).map(([key, messages]) => [key, messages[0] || "Please review this field."])));
				if (result.applicationId) { setApplicationId(result.applicationId); setSavedIncomplete(true); }
				setSubmissionError(result.error || `We could not complete the ${finalVisual ? "" : "test "}submission. Please call 916-505-5663.`);
				return;
			}
			setApplicationId(result.applicationId || ""); setStep(3);
			requestAnimationFrame(() => document.getElementById("confirmation-heading")?.focus());
		} catch {
			setSubmissionError(`We could not confirm that the ${finalVisual ? "request" : "test request"} was saved. Please call 916-505-5663.`);
		} finally { setSubmitting(false); }
	}

	function submit(event: React.FormEvent) {
		event.preventDefault();
		if (!validateBeforeSecurity()) return;
		if (!data.turnstileToken) {
			setPendingSecuritySubmission(true);
			return;
		}
		void submitVerifiedApplication();
	}

	useEffect(() => {
		if (pendingSecuritySubmission && data.turnstileToken && !submitting) void submitVerifiedApplication();
	}, [data.turnstileToken, pendingSecuritySubmission, submitting]);

	function retryTurnstile() {
		setSubmissionError("");
		setData((current) => ({ ...current, turnstileToken: "" }));
		setTurnstileStatus({ state: "loading" });
		setTurnstileRetryNonce((current) => current + 1);
	}

	function reset() {
		const next = createEmptyApplication();
		if (isReleaseCandidate) { next.challengeToken = "release-candidate-mock"; next.turnstileToken = "release-candidate-mock"; }
		setData(next); setApplicationId(""); setConsent(false); setSubmissionError(""); setSavedIncomplete(false); setPendingSecuritySubmission(false); setTurnstileStatus({ state: "loading" }); setTurnstileRetryNonce((current) => current + 1); setStep(1);
	}

	if (step === 3) return (
		<div className="confirmation-card">
			<span className="confirmation-icon"><Check /></span>
			<p className="eyebrow">{finalVisual ? "Application confirmation" : "Stage 3B test confirmation"}</p>
			<h1 id="confirmation-heading" tabIndex={-1}>We've received your request.</h1>
			<p>{isReleaseCandidate ? "Thank you. Craig will review the information and follow up about the transaction." : isProduction ? "Your request was securely saved and accepted for delivery to Cox Capital. Email delivery continues asynchronously; keep the application ID for reference." : <><strong>TEST ENVIRONMENT — this was a test submission.</strong> The synthetic request was durably stored and accepted by the email queue. Email delivery continues asynchronously; this screen does not claim final inbox delivery.</>}</p>
			{applicationId && <p className="application-id">Application ID <strong>{applicationId}</strong></p>}
			<div className="button-row"><a className="button button-primary" href="tel:+19165055663">Call 916-505-5663</a><Link className="button button-secondary" to="/">Return Home</Link><button className="text-button" onClick={reset}>Submit Another Application</button></div>
		</div>
	);

	return (
		<div className="application-shell">
			{!finalVisual && <div className="preview-form-notice" role="note"><strong>TEST ENVIRONMENT — Do not enter real borrower information.</strong><span>Only obvious synthetic test data is permitted. Valid test submissions are stored and emailed to Cox Capital's two approved business inboxes.</span></div>}
			<div className="progress" aria-label={`Step ${step} of 2`}><span className="is-complete">1</span><i className={step === 2 ? "is-complete" : ""} /><span className={step === 2 ? "is-complete" : ""}>2</span></div>
			{step === 1 ? (
				<section aria-labelledby="application-heading" className="application-card choice-step">
					<p className="eyebrow">Step 1 of 2</p><h1 id="application-heading" tabIndex={-1}>What are you looking to finance?</h1><p className="form-intro">Choose the closest fit. You can explain the details on the next step.</p>
					<div className="choice-grid" role="radiogroup" aria-label="Transaction type">
						{choices.map((choice) => <button type="button" key={choice.value} role="radio" aria-checked={data.transactionType === choice.value} className={data.transactionType === choice.value ? "choice-card is-selected" : "choice-card"} onClick={() => chooseTransaction(choice.value)}><choice.icon /><span><strong>{choice.title}</strong><small>{choice.detail}</small></span><i className="radio-dot" /></button>)}
					</div>
					<button className="button button-primary button-wide" onClick={continueToDetails}>Continue <ArrowRight size={18} /></button>
				</section>
			) : (
				<form className="application-card" onSubmit={submit} noValidate>
					<div className="form-heading"><div><p className="eyebrow">Step 2 of 2</p><h1 id="application-heading" tabIndex={-1}>Tell us about the deal.</h1><p className="form-intro">Share the essentials for an initial review. Do not include SSNs, tax IDs, bank details, or sensitive documents.</p></div><button type="button" className="back-button" onClick={() => setStep(1)}><ArrowLeft size={16} /> Back</button></div>

					<fieldset><legend>Property Address{data.propertyAddresses.length > 1 ? "es" : ""}</legend><p className="field-help">The first address is the primary property used to identify your application.</p>
						{data.propertyAddresses.map((address, index) => <div className="address-row" key={index}><label><span>{index === 0 ? "Primary Property Address" : `Additional Collateral ${index}`}</span><input value={address} onChange={(e) => updateAddress(index, e.target.value)} autoComplete="street-address" placeholder="Street, city, state, ZIP" aria-invalid={Boolean(errors.propertyAddresses)} /></label>{index > 0 && <button type="button" aria-label={`Remove additional collateral ${index}`} onClick={() => removeAddress(index)}><Trash2 size={18} /></button>}</div>)}
						<FieldError message={errors.propertyAddresses} />
						{data.propertyAddresses.length < 10 && <button type="button" className="add-button" onClick={addAddress}><Plus size={17} /> Add Another Property</button>}
					</fieldset>

					<div className="form-grid two"><label><span>Requested Loan Amount</span><input inputMode="numeric" value={currency(data.requestedLoanAmount)} onChange={(e) => update("requestedLoanAmount", fromCurrency(e.target.value))} placeholder="$0" aria-invalid={Boolean(errors.requestedLoanAmount)} /><FieldError message={errors.requestedLoanAmount} /></label><label><span>{valueLabel}</span><input inputMode="numeric" value={currency(data.propertyValue)} onChange={(e) => update("propertyValue", fromCurrency(e.target.value))} placeholder="$0" aria-invalid={Boolean(errors.propertyValue)} /><FieldError message={errors.propertyValue} /></label></div>
					<div className="form-grid two"><label><span>Desired Closing Date</span><input type="date" min={minDate} value={data.desiredClosingDate} onChange={(e) => update("desiredClosingDate", e.target.value)} aria-invalid={Boolean(errors.desiredClosingDate)} /><FieldError message={errors.desiredClosingDate} /></label><label><span>Name</span><input value={data.applicantName} onChange={(e) => update("applicantName", e.target.value)} autoComplete="name" aria-invalid={Boolean(errors.applicantName)} /><FieldError message={errors.applicantName} /></label></div>
					<div className="form-grid two"><label><span>Phone Number</span><input type="tel" inputMode="tel" value={data.phone} onChange={(e) => update("phone", formatPhone(e.target.value))} autoComplete="tel" placeholder="(916) 555-0123" aria-invalid={Boolean(errors.phone)} /><FieldError message={errors.phone} /></label><label><span>Email Address</span><input type="email" value={data.email} onChange={(e) => update("email", e.target.value)} autoComplete="email" aria-invalid={Boolean(errors.email)} /><FieldError message={errors.email} /></label></div>
					<label><span>Mailing Address</span><input value={data.mailingAddress} onChange={(e) => update("mailingAddress", e.target.value)} autoComplete="street-address" placeholder="Street, city, state, ZIP" aria-invalid={Boolean(errors.mailingAddress)} /><FieldError message={errors.mailingAddress} /></label>

					<fieldset><legend>What is the primary business or investment purpose of the loan?</legend><p className="field-help">Choose the closest fit. Craig can clarify the details during the initial review.</p><div className="select-wrap"><select required aria-label="Primary business or investment purpose" value={data.useOfProceeds} onChange={(e) => { update("useOfProceeds", e.target.value); if (e.target.value !== "Other business or investment purpose") update("useOfProceedsExplanation", ""); }} aria-invalid={Boolean(errors.useOfProceeds)}><option value="">Select one</option>{useOptions.map((option) => <option key={option}>{option}</option>)}</select></div><FieldError message={errors.useOfProceeds} />{data.useOfProceeds === "Other business or investment purpose" && <label><span>Short explanation</span><textarea rows={3} value={data.useOfProceedsExplanation} onChange={(e) => update("useOfProceedsExplanation", e.target.value)} placeholder="Briefly describe the business or investment purpose." aria-invalid={Boolean(errors.useOfProceedsExplanation)} /><FieldError message={errors.useOfProceedsExplanation} /></label>}</fieldset>

					<label><span>Tell Us About the Deal</span><textarea rows={5} value={data.dealSummary} onChange={(e) => update("dealSummary", e.target.value)} placeholder="Briefly tell us what you're buying, refinancing, building, or trying to accomplish." aria-invalid={Boolean(errors.dealSummary)} /><FieldError message={errors.dealSummary} /></label>
					<label className="honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={data.website} onChange={(e) => update("website", e.target.value)} /></label>
					<div className="application-disclaimer"><strong>Notice before submission</strong><p>This form collects only the information needed for an initial review of a potential business-purpose real-estate financing transaction. It is not a full credit application, approval, commitment to lend, rate lock, term sheet, or promise of funding. Do not include Social Security numbers, tax identification numbers, bank credentials, tax returns, bank statements, credit reports, or sensitive underwriting documents.</p><p>Your inquiry will be stored temporarily in Cox Capital’s isolated application system and delivered through its transactional email provider to Cox Capital's approved business inboxes. See the <Link to="/privacy-policy-2/">Privacy Policy</Link> for categories, purposes, vendors, retention, and request information.</p></div>
					<label className="consent-row"><input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setErrors((current) => ({ ...current, consent: "" })); }} /><span>I confirm that this request is primarily for a business or investment purpose, that the information I provided is accurate to the best of my knowledge, and that I understand this is an initial inquiry—not an approval or commitment to lend.</span></label><FieldError message={errors.consent} />
					{!isReleaseCandidate && <>
						<Turnstile onToken={handleTurnstileToken} onStatus={handleTurnstileStatus} retryNonce={turnstileRetryNonce} />
						{turnstileStatus.state === "error" && <div className="security-error" role="alert"><span>{turnstileStatus.message}</span><button type="button" className="text-button" onClick={retryTurnstile}>Try Again</button></div>}
						<FieldError message={errors.challengeToken} />
					</>}
					{submissionError && <div className="submission-error" role="alert"><strong>{submissionError}</strong>{applicationId && <span>Application ID: {applicationId}</span>}</div>}
					{!savedIncomplete && <button className="button button-primary button-wide" type="submit" disabled={submitting || !data.challengeToken}>{submitting ? "Submitting…" : pendingSecuritySubmission ? "Verifying…" : <>Submit Application <ArrowRight size={18} /></>}</button>}
					<p className="privacy-note">{finalVisual ? "This initial application is isolated from Cox Capital's servicing systems and does not request sensitive underwriting documents." : "Stage 3B is an isolated test environment. It has no Synology, servicing PostgreSQL, internal API, CRM, analytics, or production-site connection."}</p>
				</form>
			)}
		</div>
	);
}
